import React, { useMemo, useRef, useEffect, useState, useCallback, memo, type CSSProperties } from "react";
import { FREE_WIN_PLAYER_ID, ROUND_STATES } from "../../../util/Constants";
import { getLoggedUser } from "../../../util/SecurityUtils";
import { useTranslation } from "react-i18next";
import MatchBox from "../../matches/MatchBox";
import "./PlayoffBracketTab.css";
import type { TFunction } from "i18next";
import type { Match, TournamentDetail, TournamentRound } from "../../../types/domain";

interface MatchPosition {
  top: number;
  bottom: number;
  right: number;
  left: number;
  centerY: number;
}

interface BracketRound {
  roundIndex: number;
  roundOrder: number;
  state: TournamentRound["state"];
  matches: Match[];
}

interface BracketData {
  bracketId: number;
  bracketType: string;
  rounds: BracketRound[];
}

interface SingleBracketProps {
  bracket: BracketData;
  playoffRounds: TournamentRound[];
  onMatchClick: (match: Match) => void;
  showTitle: boolean;
  isLosersBracket: boolean;
  canManage: boolean;
  t: TFunction;
}

// Separate component for each bracket to handle its own ref
const SingleBracket = memo(function SingleBracket({ bracket, playoffRounds, onMatchClick, showTitle, isLosersBracket, canManage, t }: SingleBracketProps) {
  const [matchPositions, setMatchPositions] = useState<Record<string, MatchPosition>>({});
  const [positionPass, setPositionPass] = useState(0);
  const bracketRef = useRef<HTMLDivElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const handleMatchClick = useCallback((match: Match) => {
    const matchRound = playoffRounds?.find((r: TournamentRound) => r.matches.some((m: Match) => m.id === match.id));
    if (!matchRound || matchRound.state !== ROUND_STATES.IN_PROGRESS) return;

    const loggedUser = getLoggedUser();
    const isPlayersMatch = match.playerOne?.id === loggedUser?.playerId ||
                           match.playerTwo?.id === loggedUser?.playerId;
    const isUserAuthorizedToEditMatch = canManage || isPlayersMatch;
    const isByeMatch = match.playerOne?.id === FREE_WIN_PLAYER_ID ||
                       match.playerTwo?.id === FREE_WIN_PLAYER_ID;

    if (isUserAuthorizedToEditMatch && !isByeMatch && onMatchClick) {
      onMatchClick(match);
    }
  }, [playoffRounds, onMatchClick, canManage]);

  const isMatchRoundInProgress = useCallback((match: Match) => {
    const matchRound = playoffRounds?.find((r: TournamentRound) => r.matches.some((m: Match) => m.id === match.id));
    return matchRound?.state === ROUND_STATES.IN_PROGRESS;
  }, [playoffRounds]);

  const bracketStructureKey = useMemo(() => {
    const matchIds = bracket.rounds.flatMap((r: BracketRound) => r.matches.map((m: Match) => m.id)).join(',');
    const roundCount = bracket.rounds.length;
    return `${bracket.bracketId}-${roundCount}-${matchIds}`;
  }, [bracket]);

  // Reset position pass when bracket structure changes (not when results change)
  useEffect(() => {
    setPositionPass(0);
    setMatchPositions({});
  }, [bracketStructureKey]);

  // Calculate match positions for drawing connectors
  // We need two passes: first to position later rounds, second to get final positions for connectors
  useEffect(() => {
    if (!wrapperRef.current) return;

    const calculatePositions = (): Record<string, MatchPosition> => {
      const positions: Record<string, MatchPosition> = {};
      const wrapper = wrapperRef.current;
      if (!wrapper) return positions;
      const matchElements = wrapper.querySelectorAll('[data-match-id]');

      matchElements.forEach((el: Element) => {
        const matchId = el.getAttribute('data-match-id');
        if (matchId == null) return;
        const rect = el.getBoundingClientRect();
        const parentRect = wrapper.getBoundingClientRect();

        positions[matchId] = {
          top: rect.top - parentRect.top,
          bottom: rect.bottom - parentRect.top,
          right: rect.right - parentRect.left,
          left: rect.left - parentRect.left,
          centerY: (rect.top + rect.bottom) / 2 - parentRect.top
        };
      });

      return positions;
    };

    const timer = setTimeout(() => {
      setMatchPositions(calculatePositions());

      // Trigger additional passes to recalculate after repositioning
      // More passes needed for deeper brackets (like Grand Final after WB Final)
      if (positionPass < 4) {
        setPositionPass(p => p + 1);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [positionPass]);

  // Auto-scroll to the right on initial load to show the most recent round
  useEffect(() => {
    if (!bracketRef.current) return;

    const timer = setTimeout(() => {
      const scrollContainer = bracketRef.current;
      if (scrollContainer) {
        // Scroll to 64px from the right edge so the last column is fully visible
        const maxScroll = scrollContainer.scrollWidth - scrollContainer.clientWidth;
        scrollContainer.scrollLeft = Math.max(0, maxScroll - 32);
      }
    }, 150);

    return () => clearTimeout(timer);
  }, []);

  const { bracketId, rounds } = bracket;
  const baseGap = 16;

  const getRoundLabel = (roundIndex: number) => {
    return `${t("round")} ${roundIndex}`;
  };

  // Generate connector paths
  const renderConnectors = (): React.ReactElement[] => {
    const paths: React.ReactElement[] = [];
    const connectorWidth = 50;
    const halfConnector = connectorWidth / 2;

    bracket.rounds.forEach((round: BracketRound, colIndex: number) => {
      if (colIndex === bracket.rounds.length - 1) return;

      const nextRound = bracket.rounds[colIndex + 1];
      if (!nextRound) return;

      const curr = round.matches.length;
      const next = nextRound.matches.length;

      if (!isLosersBracket) {
        // Winners bracket connections
        // Check if next round contains Grand Final or Bracket Reset
        const nextIsGrandFinal = nextRound.matches.some((m: Match) =>
          m.bracketType === "GRAND_FINAL" || m.bracketType === "BRACKET_RESET"
        );

        // Check if next round has third place match (final + third place scenario)
        const nextHasThirdPlace = nextRound.matches.some((m: Match) => m.bracketType === "THIRD_PLACE");
        const finalMatch = nextRound.matches.find((m: Match) => m.bracketType !== "THIRD_PLACE");

        if (nextIsGrandFinal && curr === 1 && next === 1) {
          // Simple 1:1 horizontal connection to Grand Final
          const match = round.matches[0];
          const nextMatch = nextRound.matches[0];
          const pos = matchPositions[match?.id];
          const posNext = matchPositions[nextMatch?.id];

          if (pos && posNext) {
            paths.push(
              <line key={`gf-h-${match.id}`} x1={pos.right} y1={pos.centerY} x2={posNext.left} y2={posNext.centerY} stroke="var(--border-color)" strokeWidth="2" />
            );
          }
        } else if (nextHasThirdPlace && curr === 2 && finalMatch) {
          // Special case: 2 semifinals -> final + third place match
          // Connect both semifinals to the final (winners go to final)
          const match1 = round.matches[0];
          const match2 = round.matches[1];
          const pos1 = matchPositions[match1?.id];
          const pos2 = matchPositions[match2?.id];
          const posFinal = matchPositions[finalMatch?.id];

          if (pos1 && pos2 && posFinal) {
            // Draw connections to final
            paths.push(
              <line key={`h1-wb-${match1?.id}`} x1={pos1.right} y1={pos1.centerY} x2={pos1.right + halfConnector} y2={pos1.centerY} stroke="var(--border-color)" strokeWidth="2" />
            );
            paths.push(
              <line key={`h2-wb-${match2?.id}`} x1={pos2.right} y1={pos2.centerY} x2={pos2.right + halfConnector} y2={pos2.centerY} stroke="var(--border-color)" strokeWidth="2" />
            );
            paths.push(
              <line key={`v-wb-${match1?.id}-${match2?.id}`} x1={pos1.right + halfConnector} y1={pos1.centerY} x2={pos1.right + halfConnector} y2={pos2.centerY} stroke="var(--border-color)" strokeWidth="2" />
            );
            const centerY = (pos1.centerY + pos2.centerY) / 2;
            paths.push(
              <line key={`h3-wb-${match1?.id}-${match2?.id}`} x1={pos1.right + halfConnector} y1={centerY} x2={posFinal.left} y2={posFinal.centerY} stroke="var(--border-color)" strokeWidth="2" />
            );
          }
        } else {
          // Standard 2:1 reduction connections
          for (let i = 0; i < next; i++) {
            const match1 = round.matches[i * 2];
            const match2 = round.matches[i * 2 + 1];
            const nextMatch = nextRound.matches[i];

            const pos1 = matchPositions[match1?.id];
            const pos2 = match2 ? matchPositions[match2?.id] : null;
            const posNext = nextMatch ? matchPositions[nextMatch?.id] : null;

            if (!pos1) continue;

            // Draw standard reduction connector
            paths.push(
              <line key={`h1-wb-${match1?.id}`} x1={pos1.right} y1={pos1.centerY} x2={pos1.right + halfConnector} y2={pos1.centerY} stroke="var(--border-color)" strokeWidth="2" />
            );

            if (pos2) {
              paths.push(
                <line key={`h2-wb-${match2?.id}`} x1={pos2.right} y1={pos2.centerY} x2={pos2.right + halfConnector} y2={pos2.centerY} stroke="var(--border-color)" strokeWidth="2" />
              );
              paths.push(
                <line key={`v-wb-${match1?.id}-${match2?.id}`} x1={pos1.right + halfConnector} y1={pos1.centerY} x2={pos1.right + halfConnector} y2={pos2.centerY} stroke="var(--border-color)" strokeWidth="2" />
              );
              const centerY = (pos1.centerY + pos2.centerY) / 2;
              if (posNext) {
                paths.push(
                  <line key={`h3-wb-${match1?.id}-${match2?.id}`} x1={pos1.right + halfConnector} y1={centerY} x2={posNext.left} y2={centerY} stroke="var(--border-color)" strokeWidth="2" />
                );
              }
            } else if (posNext) {
              paths.push(
                <line key={`h3-wb-single-${match1?.id}`} x1={pos1.right + halfConnector} y1={pos1.centerY} x2={posNext.left} y2={posNext.centerY} stroke="var(--border-color)" strokeWidth="2" />
              );
            }
          }
        }
        return;
      }

      // Losers bracket: draw connectors from each source match to its target
      // Determine if this is a 1:1 (dropout) or 2:1 (reduction) round
      const isDropoutRound = curr === next;
      const isReductionRound = curr === next * 2;

      if (isDropoutRound) {
        // 1:1 connections - each source connects to corresponding target
        for (let i = 0; i < curr; i++) {
          const match = round.matches[i];
          const nextMatch = nextRound.matches[i];
          const pos = matchPositions[match?.id];
          const posNext = matchPositions[nextMatch?.id];

          if (!pos || !posNext) continue;

          // Horizontal from source
          paths.push(
            <line key={`lb-h-${match.id}`} x1={pos.right} y1={pos.centerY} x2={pos.right + halfConnector} y2={pos.centerY} stroke="var(--border-color)" strokeWidth="2" />
          );

          // Vertical line (red to indicate WB player joins)
          const minY = Math.min(pos.centerY, posNext.centerY);
          const maxY = Math.max(pos.centerY, posNext.centerY);
          paths.push(
            <line key={`lb-v-${match.id}`} x1={pos.right + halfConnector} y1={minY} x2={pos.right + halfConnector} y2={maxY} stroke="#DC3545" strokeWidth="2" />
          );

          // Horizontal to target
          paths.push(
            <line key={`lb-to-${match.id}`} x1={pos.right + halfConnector} y1={posNext.centerY} x2={posNext.left} y2={posNext.centerY} stroke="var(--border-color)" strokeWidth="2" />
          );
        }
      } else if (isReductionRound) {
        // 2:1 connections - pairs of sources connect to one target
        for (let i = 0; i < next; i++) {
          const match1 = round.matches[i * 2];
          const match2 = round.matches[i * 2 + 1];
          const nextMatch = nextRound.matches[i];

          const pos1 = matchPositions[match1?.id];
          const pos2 = matchPositions[match2?.id];
          const posNext = matchPositions[nextMatch?.id];

          if (!pos1 || !posNext) continue;

          paths.push(
            <line key={`lb-h1-${match1.id}`} x1={pos1.right} y1={pos1.centerY} x2={pos1.right + halfConnector} y2={pos1.centerY} stroke="var(--border-color)" strokeWidth="2" />
          );

          if (pos2) {
            paths.push(
              <line key={`lb-h2-${match2.id}`} x1={pos2.right} y1={pos2.centerY} x2={pos2.right + halfConnector} y2={pos2.centerY} stroke="var(--border-color)" strokeWidth="2" />
            );
            paths.push(
              <line key={`lb-v-${match1.id}-${match2.id}`} x1={pos1.right + halfConnector} y1={pos1.centerY} x2={pos1.right + halfConnector} y2={pos2.centerY} stroke="var(--border-color)" strokeWidth="2" />
            );
            const centerY = (pos1.centerY + pos2.centerY) / 2;
            paths.push(
              <line key={`lb-h3-${match1.id}-${match2.id}`} x1={pos1.right + halfConnector} y1={centerY} x2={posNext.left} y2={centerY} stroke="var(--border-color)" strokeWidth="2" />
            );
          } else {
            paths.push(
              <line key={`lb-to-single-${match1.id}`} x1={pos1.right + halfConnector} y1={pos1.centerY} x2={posNext.left} y2={posNext.centerY} stroke="var(--border-color)" strokeWidth="2" />
            );
          }
        }
      } else {
        // Mixed or other ratio - connect each source to closest target
        for (let i = 0; i < curr; i++) {
          const match = round.matches[i];
          const pos = matchPositions[match?.id];
          if (!pos) continue;

          // Find closest target by position
          let closestTarget = null;
          let closestDist = Infinity;
          for (let j = 0; j < next; j++) {
            const targetPos = matchPositions[nextRound.matches[j]?.id];
            if (targetPos) {
              const dist = Math.abs(targetPos.centerY - pos.centerY);
              if (dist < closestDist) {
                closestDist = dist;
                closestTarget = { match: nextRound.matches[j], pos: targetPos };
              }
            }
          }

          if (!closestTarget) continue;
          const posNext = closestTarget.pos;

          // Draw simple connection
          paths.push(
            <line key={`lb-h-mix-${match.id}`} x1={pos.right} y1={pos.centerY} x2={pos.right + halfConnector} y2={pos.centerY} stroke="var(--border-color)" strokeWidth="2" />
          );
          paths.push(
            <line key={`lb-v-mix-${match.id}`} x1={pos.right + halfConnector} y1={pos.centerY} x2={pos.right + halfConnector} y2={posNext.centerY} stroke="var(--border-color)" strokeWidth="2" />
          );
          paths.push(
            <line key={`lb-to-mix-${match.id}`} x1={pos.right + halfConnector} y1={posNext.centerY} x2={posNext.left} y2={posNext.centerY} stroke="var(--border-color)" strokeWidth="2" />
          );
        }
      }
    });

    return paths;
  };

  // Get estimated match height
  const getMatchHeight = () => {
    const matchElement = wrapperRef.current?.querySelector<HTMLElement>('[data-match-id]');
    return matchElement ? matchElement.offsetHeight : 85;
  };

  // Calculate match position based on actual rendered positions of source matches
  // Simplified: use same logic for both winners and losers brackets
  const calculateMatchStyle = (colIndex: number, matchIndex: number, totalMatchesInRound: number, round: BracketRound, match: Match): CSSProperties => {
    const matchHeight = getMatchHeight();

    // First round: simple margin-based spacing
    if (colIndex === 0) {
      return {
        marginBottom: matchIndex < totalMatchesInRound - 1 ? `${baseGap}px` : '0'
      };
    }

    const prevRound = rounds[colIndex - 1];
    if (!prevRound) return {};

    const columnElement = wrapperRef.current?.querySelectorAll<HTMLElement>('.bracket-round-column')[colIndex];
    const headerElement = columnElement?.querySelector<HTMLElement>('.bracket-round-header');
    const headerHeight = headerElement ? headerElement.offsetHeight + 16 : 0;

    // Special handling for third place match - position below the final
    if (match?.bracketType === "THIRD_PLACE") {
      // Find the final match position in this round
      const finalMatch = round.matches.find((m: Match) => m.bracketType !== "THIRD_PLACE");
      const finalPos = finalMatch ? matchPositions[finalMatch.id] : null;

      if (finalPos) {
        // Position third place match below the final with some gap
        const topPosition = finalPos.bottom + baseGap - headerHeight;
        return {
          position: 'absolute',
          top: `${Math.max(0, topPosition)}px`,
          left: 0,
          right: 0
        };
      }

      // Fallback: position based on previous round if final position not available
      const prevCount = prevRound.matches.length;
      if (prevCount >= 2) {
        const pos1 = matchPositions[prevRound.matches[0]?.id];
        const pos2 = matchPositions[prevRound.matches[1]?.id];
        if (pos1 && pos2) {
          // Position below center (where final would be)
          const centerY = (pos1.centerY + pos2.centerY) / 2;
          const topPosition = centerY + matchHeight / 2 + baseGap - headerHeight;
          return {
            position: 'absolute' as const,
            top: `${Math.max(0, topPosition)}px`,
            left: 0,
            right: 0
          };
        }
      }

      return { marginBottom: '0' };
    }

    // For all later rounds: position based on source matches (standard bracket centering)
    // Each match in current round is fed by 1 or 2 matches from previous round
    const prevCount = prevRound.matches.length;
    // For rounds with third place match, only count non-third-place matches
    const currCountExcludingThirdPlace = round.matches.filter((m: Match) => m.bracketType !== "THIRD_PLACE").length;
    const currCount = currCountExcludingThirdPlace > 0 ? currCountExcludingThirdPlace : totalMatchesInRound;

    // Calculate which previous match(es) feed into this one
    const ratio = prevCount / currCount;
    const matchIndexInNonThirdPlace = round.matches.filter((m: Match) => m.bracketType !== "THIRD_PLACE").indexOf(match);
    const effectiveMatchIndex = matchIndexInNonThirdPlace >= 0 ? matchIndexInNonThirdPlace : matchIndex;
    const startIdx = Math.floor(effectiveMatchIndex * ratio);
    const endIdx = Math.min(Math.floor((effectiveMatchIndex + 1) * ratio), prevCount) - 1;

    const sourceMatch1 = prevRound.matches[startIdx];
    const sourceMatch2 = endIdx > startIdx ? prevRound.matches[endIdx] : null;

    const pos1 = sourceMatch1 ? matchPositions[sourceMatch1.id] : null;
    const pos2 = sourceMatch2 ? matchPositions[sourceMatch2.id] : null;

    if (!pos1) {
      // Fallback: don't use absolute positioning until positions are calculated
      // This prevents incorrect positioning on first render pass
      return { marginBottom: '0' };
    }

    // Calculate the center Y position
    const centerY = pos2
      ? (pos1.centerY + pos2.centerY) / 2
      : pos1.centerY;

    const topPosition = centerY - matchHeight / 2 - headerHeight;

    return {
      position: 'absolute' as const,
      top: `${Math.max(0, topPosition)}px`,
      left: 0,
      right: 0
    };
  };

  // Calculate container height based on first round
  const getContainerHeight = () => {
    const firstRound = rounds[0];
    if (!firstRound) return 'auto';

    // Get actual height from DOM if available
    const matchElement = wrapperRef.current?.querySelector<HTMLElement>('[data-match-id]');
    const matchHeight = matchElement ? matchElement.offsetHeight : 85;

    return firstRound.matches.length * matchHeight + (firstRound.matches.length - 1) * baseGap;
  };

  return (
    <div className="playoff-bracket-container mb-5">
      {showTitle && (
        <h5 className="mb-3" style={{ color: 'var(--text-primary)' }}>
          {t("playoffBracket")} {bracketId}
        </h5>
      )}

      <div className="bracket-grid" ref={bracketRef}>
        {/* Wrapper that contains both SVG and matches, ensuring SVG covers full scrollable area */}
        <div className={`bracket-rounds-wrapper ${isLosersBracket ? 'losers-bracket' : ''}`} ref={wrapperRef} style={{ position: 'relative' }}>
          {/* SVG layer for connectors - now inside the scrollable wrapper */}
          <svg
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              overflow: 'visible'
            }}
          >
            {renderConnectors()}
          </svg>
          {rounds.map((round: BracketRound, colIndex: number) => (
            <div key={round.roundOrder} className="bracket-round-column">
              <div className="bracket-round-header">
                {getRoundLabel(round.roundIndex)}
              </div>

              <div
                className="bracket-matches"
                style={colIndex > 0 ? { position: 'relative', minHeight: getContainerHeight() } : {}}
              >
                {round.matches.map((match: Match, matchIndex: number) => {
                  // Determine special styling based on bracketType
                  const isGoldMatch = match.bracketType === "GRAND_FINAL" || match.bracketType === "BRACKET_RESET";
                  const isBronzeMatch = match.bracketType === "THIRD_PLACE";
                  const isFinalMatch = !match.bracketType && round.matches.some((m: Match) => m.bracketType === "THIRD_PLACE") && match.bracketType !== "THIRD_PLACE";

                  let specialClass = "";
                  if (isGoldMatch) specialClass = "gold-match";
                  else if (isBronzeMatch) specialClass = "bronze-match";
                  else if (isFinalMatch) specialClass = "gold-match";

                  // Determine label for special matches (only for third place)
                  let matchLabel = null;
                  if (isBronzeMatch) {
                    matchLabel = t("thirdPlaceMatch");
                  }

                  // Determine which player to dim based on match result
                  const matchState = match.state;
                  const isFinished = matchState === "PLAYER_ONE_WON" || matchState === "PLAYER_TWO_WON" ||
                                     matchState === "PLAYER_ONE_DEFAULT" || matchState === "PLAYER_TWO_DEFAULT";

                  // Dim the loser (winner advances)
                  const dimClass = isFinished ? "dim-loser" : "";

                  // Determine who won for CSS targeting
                  const playerOneWon = matchState === "PLAYER_ONE_WON" || matchState === "PLAYER_ONE_DEFAULT";

                  return (
                    <div
                      key={match.id}
                      data-match-id={match.id}
                      className="bracket-match-item"
                      style={calculateMatchStyle(colIndex, matchIndex, round.matches.length, round, match)}
                    >
                      {matchLabel && (
                        <div style={{
                          textAlign: 'center',
                          fontSize: '0.85rem',
                          fontWeight: 'bold',
                          color: isBronzeMatch ? '#CD7F32' : '#FFD700',
                          marginBottom: '4px'
                        }}>
                          {matchLabel}
                        </div>
                      )}
                      <div
                        className={`compact-matchbox ${specialClass} ${dimClass}`}
                        data-player-one-won={playerOneWon ? "true" : "false"}
                        data-finished={isFinished ? "true" : "false"}
                      >
                        <MatchBox
                          match={match}
                          onClick={() => handleMatchClick(match)}
                          indicateFinished={isMatchRoundInProgress(match)}
                          canManage={canManage}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}, (prevProps: SingleBracketProps, nextProps: SingleBracketProps) => {
  const prevMatchIds = prevProps.bracket.rounds.flatMap((r: BracketRound) => r.matches.map((m: Match) => `${m.id}-${m.state}`)).join(',');
  const nextMatchIds = nextProps.bracket.rounds.flatMap((r: BracketRound) => r.matches.map((m: Match) => `${m.id}-${m.state}`)).join(',');
  return prevMatchIds === nextMatchIds &&
         prevProps.isLosersBracket === nextProps.isLosersBracket &&
         prevProps.showTitle === nextProps.showTitle &&
         prevProps.canManage === nextProps.canManage;
});

interface PlayoffBracketTabProps {
  playoffRounds: TournamentRound[];
  tournament: TournamentDetail;
  onMatchClick: (match: Match) => void;
}

function PlayoffBracketTab({ playoffRounds, tournament, onMatchClick }: PlayoffBracketTabProps) {
  const { t } = useTranslation();
  const canManage = tournament.canManage;

  // Winners bracket includes: WINNERS, GRAND_FINAL, BRACKET_RESET
  const WINNERS_BRACKET_TYPES = ["WINNERS", "GRAND_FINAL", "BRACKET_RESET"];
  // Losers bracket includes: LOSERS, THIRD_PLACE
  const LOSERS_BRACKET_TYPES = ["LOSERS", "THIRD_PLACE"];

  // Group matches by bracketId, then by bracketType (WINNERS/LOSERS)
  const brackets = useMemo(() => {
    if (!playoffRounds || playoffRounds.length === 0) return [];

    const bracketIds = new Set<number>();
    playoffRounds.forEach((round: TournamentRound) => {
      round.matches.forEach((match: Match) => {
        bracketIds.add(match.bracketId || 1);
      });
    });

    return Array.from(bracketIds)
      .sort((a: number, b: number) => a - b)
      .map((bracketId: number) => {
        const allMatches = playoffRounds.flatMap((r: TournamentRound) => r.matches.filter((m: Match) => (m.bracketId || 1) === bracketId));
        const hasLosersBracket = allMatches.some((m: Match) => m.bracketType === "LOSERS");

        const createWinnersRounds = (): BracketRound[] => {
          return playoffRounds.map((round: TournamentRound, roundIndex: number) => ({
            roundIndex: roundIndex + 1,
            roundOrder: round.roundOrder,
            state: round.state,
            matches: round.matches
              .filter((match: Match) => {
                if ((match.bracketId || 1) !== bracketId) return false;
                if (!hasLosersBracket) return true;
                return (match.bracketType != null && WINNERS_BRACKET_TYPES.includes(match.bracketType)) || !match.bracketType;
              })
              .sort((a: Match, b: Match) => {
                if (a.bracketType === "THIRD_PLACE" && b.bracketType !== "THIRD_PLACE") return 1;
                if (b.bracketType === "THIRD_PLACE" && a.bracketType !== "THIRD_PLACE") return -1;
                return (a.bracketPosition || 0) - (b.bracketPosition || 0);
              })
          })).filter((round: BracketRound) => round.matches.length > 0);
        };

        const createLosersRounds = (): BracketRound[] => {
          return playoffRounds.map((round: TournamentRound, roundIndex: number) => {
            const losersMatches = round.matches
              .filter((match: Match) => {
                if ((match.bracketId || 1) !== bracketId) return false;
                return match.bracketType != null && LOSERS_BRACKET_TYPES.includes(match.bracketType);
              })
              .sort((a: Match, b: Match) => (a.bracketPosition || 0) - (b.bracketPosition || 0));

            return {
              roundIndex: roundIndex + 1,
              roundOrder: round.roundOrder,
              state: round.state,
              matches: losersMatches
            };
          }).filter(round => round.matches.length > 0);
        };

        return {
          bracketId,
          hasLosersBracket,
          winnersBracket: {
            bracketId,
            bracketType: "WINNERS",
            rounds: createWinnersRounds()
          },
          losersBracket: hasLosersBracket ? {
            bracketId,
            bracketType: "LOSERS",
            rounds: createLosersRounds()
          } : null
        };
      });
  }, [playoffRounds]);

  if (!playoffRounds || playoffRounds.length === 0) {
    return <div className="text-center p-4">{t("loading")}</div>;
  }

  return (
    <div className="playoff-bracket-tab">
      {brackets.map(bracket => (
        <div key={bracket.bracketId} className="bracket-group mb-4">
          {/* Show bracket title if multiple brackets */}
          {brackets.length > 1 && (
            <h5 className="mb-3" style={{ color: 'var(--text-primary)' }}>
              {t("playoffBracket")} {bracket.bracketId}
            </h5>
          )}

          {/* Winners Bracket */}
          {bracket.winnersBracket.rounds.length > 0 && (
            <>
              {bracket.hasLosersBracket && (
                <h6 className="mb-2" style={{ color: 'var(--text-secondary)' }}>
                  {t("winnersBracket")}
                </h6>
              )}
              <SingleBracket
                bracket={bracket.winnersBracket}
                playoffRounds={playoffRounds}
                onMatchClick={onMatchClick}
                showTitle={false}
                isLosersBracket={false}
                canManage={canManage}
                t={t}
              />
            </>
          )}

          {/* Losers Bracket */}
          {bracket.losersBracket && bracket.losersBracket.rounds.length > 0 && (
            <>
              <h6 className="mb-2 mt-4" style={{ color: 'var(--text-secondary)' }}>
                {t("losersBracket")}
              </h6>
              <SingleBracket
                bracket={bracket.losersBracket}
                playoffRounds={playoffRounds}
                onMatchClick={onMatchClick}
                showTitle={false}
                isLosersBracket={true}
                canManage={canManage}
                t={t}
              />
            </>
          )}
        </div>
      ))}
    </div>
  );
}

export default PlayoffBracketTab;
